import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, of, takeUntil, timeout } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { AgGridTableService } from '../../../shared/services/ag-grid.service';
import { ApiCallsService } from '../../../shared/services/api-calls.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ToastService } from '../../../shared/services/toast.service';
import {
  TableSchemaService,
  ConfigColumn,
  ModalFormSchema,
  TableConfigColumn,
  TableConfigEntry
} from '../../../shared/services/table-schema';

@Injectable({
  providedIn: 'root'
})
export class ConfigPaginationService {

  private payload: any | null = null;
  private reportKey!: string;

  private currentPage = 1;
  private limit = 10;          // default limit
  private offset = 0;          // default offset
  private totalCount = 0;
  private fullListData: any[] = [];

  totalCount$ = new BehaviorSubject<number>(0);
  payloadChanged$ = new BehaviorSubject<boolean>(false);
  // Add/Edit modal form fields built from the most recent schema-type
  // response for the current reportKey. Null when no schema is known yet.
  formSchema$ = new BehaviorSubject<ModalFormSchema | null>(null);
  private mapByUrlCache = new Map<string, any>();
  // Cached schema columns per reportKey (apiUrl), so a later normal
  // response for the same tab can still merge/repopulate the form without
  // needing the schema response to arrive again.
  private schemaColumnsByReportKey = new Map<string, { [key: string]: ConfigColumn }>();

  constructor(
    private api: ApiCallsService,
    private aggrid: AgGridTableService,
    private http: HttpClient,
    private toast: ToastService,
    private tableSchema: TableSchemaService
  ) { }

  downloadheader: string = ''
  enableEdit: boolean = false
  enableCheckbox: boolean = false
  allowHeaderSelectAll: boolean = true
  /** Initial load */
  // keyed by `${reportKey}:${configId}`
  private dataUrlBySchemaKey = new Map<string, string>();
  private headerBySchemaKey = new Map<string, any[]>();
  private addEditUrlBySchemaKey = new Map<string, { add: string; edit: string }>();
  private tableMetaBySchemaKey = new Map<string, { title: string; add: boolean; edit: boolean; csv: boolean; pdf: boolean, add_routeurl: string, edit_routeurl: string, filter?: any, load_after_filter?: boolean, pagination?: boolean }>();
  private payloadBySchemaKey = new Map<string, any>();
  private addEditPayloadBySchemaKey = new Map<string, { addPayload?: any; editPayload?: any }>();
  private formSchemaBySchemaKey = new Map<string, ModalFormSchema>();

  // Resolves 'Table-Configuration' (or any schema-driven apiUrl) to its
  // real dataUrl for the currently-active tab. Falls back to reportKey
  // itself for tabs that were never schema-driven to begin with.
  private getEffectiveDataUrl(): string {
    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    return this.dataUrlBySchemaKey.get(key) || this.reportKey;
  }

  getTableMeta(reportKey: string, configId: string): { title: string; add: boolean; edit: boolean; csv: boolean; pdf: boolean, add_routeurl: string, edit_routeurl: string, filter?: any, load_after_filter?: boolean, pagination?: boolean } | null {
    return this.tableMetaBySchemaKey.get(this.schemaKey(reportKey, configId)) || null;
  }

  getAddEditPayloads(reportKey: string, configId: string): { addPayload?: any; editPayload?: any } | null {
    return this.addEditPayloadBySchemaKey.get(this.schemaKey(reportKey, configId)) || null;
  }

  private schemaKey(reportKey: string, configId: string): string {
    return `${reportKey}:${configId || ''}`;
  }

  initFilters(
    payload: any,
    reportKey: string,
    destroy$: any,
    downloadheader: string = '',
    EditEnable: boolean = false,
    enableCheckbox: boolean = false,
    allowHeaderSelectAll: boolean = true
  ) {
    const payloadChanged = this.isPayloadChanged(this.payload, payload)
    if (reportKey !== this.reportKey) {
      this.reset();
    }
    this.payloadChanged$.next(payloadChanged);
    this.enableEdit = EditEnable
    this.enableCheckbox = enableCheckbox
    this.allowHeaderSelectAll = allowHeaderSelectAll
    if (payloadChanged) {
      this.reset();
    }
    if (reportKey === '') {
      return
    }
    this.downloadheader = downloadheader

    const configId = payload?.config_id || '';
    const key = this.schemaKey(reportKey, configId);
    const meta = this.tableMetaBySchemaKey.get(key);
    const configuredPayload = this.payloadBySchemaKey.get(key);
    const isPaginationDisabled = meta?.pagination === false || configuredPayload?.Limit === 'ALL' || payload?.Limit === 'ALL';

    if (isPaginationDisabled) {
      const merged = { ...payload, ...(configuredPayload || {}) };
      merged.Limit = (configuredPayload?.Limit ?? payload?.Limit) || 'ALL';
      delete merged.Offset;
      delete merged.per_page;
      delete merged.page;
      this.payload = merged;
    } else {
      this.payload = {
        ...payload,
        Limit: this.limit,
        Offset: this.offset,
        per_page: this.limit,
        page: this.currentPage,
        ...(configuredPayload || {})
      };
    }
    this.reportKey = reportKey;

    // Repopulate formSchema$ from cache for this tab, since reset() just
    // nulled it and this tab's schema may already be known.
    const cachedSchema = this.formSchemaBySchemaKey.get(this.schemaKey(reportKey, configId));
    this.formSchema$.next(cachedSchema || null);

    this.fetchPage(destroy$);
  }

  /** Pagination handler (component stays unchanged) */
  changePage(
    event: { page: number; pageSize: number },
    destroy$: any
  ) {
    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    const meta = this.tableMetaBySchemaKey.get(key);
    const extraPayload = this.payloadBySchemaKey.get(key);
    const isPaginationDisabled = meta?.pagination === false || extraPayload?.Limit === 'ALL' || this.payload?.Limit === 'ALL';

    if (isPaginationDisabled) {
      return;
    }

    this.currentPage = event.page;
    this.limit = event.pageSize;
    console.log(event);
    this.payload.Limit = this.limit
    this.payload.per_page = this.limit
    this.payload.page = this.currentPage
    // ✅ correct offset calculation
    this.offset = (this.currentPage - 1) * this.limit;
    this.payload.Offset = this.offset

    if (this.offset >= this.totalCount && this.totalCount > 0) return;

    this.fetchPage(destroy$);
  }

  /** Reset */
  reset() {
    console.log('resetting');

    this.totalCount$.next(0);

    this.fullListData = [];
    this.currentPage = 1;
    this.limit = 10;
    this.offset = 0;
    this.totalCount = 0;
    // Clear the currently-displayed form schema. If a cached schema exists
    // for the (new) reportKey, processResponse() will repopulate it from
    // cache as soon as the next normal response comes back — no need to
    // wait for another schema-type response.
    this.formSchema$.next(null);

    this.aggrid.processTableData([]);
  }

  // ---------------- PRIVATE ---------------- //

  private fetchPage(destroy$: any) {
    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    const meta = this.tableMetaBySchemaKey.get(key);
    const extraPayload = this.payloadBySchemaKey.get(key);
    const isPaginationDisabled = meta?.pagination === false || extraPayload?.Limit === 'ALL' || this.payload?.Limit === 'ALL';

    let effectivePayload: any;
    if (isPaginationDisabled) {
      effectivePayload = {
        ...this.payload,
        ...(extraPayload || {})
      };
      effectivePayload.Limit = (extraPayload?.Limit ?? this.payload?.Limit) || 'ALL';
      delete effectivePayload.Offset;
      delete effectivePayload.per_page;
      delete effectivePayload.page;
    } else {
      effectivePayload = extraPayload && typeof extraPayload === 'object'
        ? { ...extraPayload, ...this.payload }
        : this.payload;
    }

    this.api.TowerDetailsTableData(
      this.getEffectiveDataUrl(),
      effectivePayload
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.processResponse(data),
        error: (err) => {
          console.error(err.error?.Message || err?.message || err);
          this.aggrid.processTableData([]);
        }
      });
  }

  private schemaFetchApiUrls = new Set<string>(['Table-Configuration']);

  needsSchemaFetch(apiUrl: string, configId: string = ''): boolean {
    return this.schemaFetchApiUrls.has(apiUrl);
  }

  getAddEditUrls(reportKey: string, configId: string): { add: string; edit: string } | null {
    return this.addEditUrlBySchemaKey.get(this.schemaKey(reportKey, configId)) || null;
  }

  /**
   * Parses a Table-Configuration lookup response and caches everything the
   * pagination/table/modal layer needs for this reportKey+configId: the
   * resolved dataUrl, the built header, add/edit urls, table meta flags, and
   * the modal form schema.
   *
   * Header/modal building itself now lives in TableSchemaService so other
   * services/components can reuse it. Because dropdownUrl columns require an
   * API call to populate their options, this is async — formSchema$ is
   * updated once that resolves, so callers don't need to await this unless
   * they specifically need the schema synchronously afterwards.
   */
  async setSchemaFromResponse(
    reportKey: string,
    configId: string,
    data: any,
    siteId?: string | number
  ): Promise<void> {
    const entry: TableConfigEntry | undefined = Array.isArray(data?.Data) ? data.Data[0] : data?.Data;
    const columns: { [key: string]: TableConfigColumn } = entry?.list?.columns || {};
    const dataUrl = this.tableSchema.stripLeadingSlash(entry?.list?.dataUrl || '');
    const key = this.schemaKey(reportKey, configId);

    this.dataUrlBySchemaKey.set(key, dataUrl);
    this.headerBySchemaKey.set(key, this.tableSchema.buildHeaderFromColumns(columns));
    this.addEditUrlBySchemaKey.set(key, {
      add: this.tableSchema.stripLeadingSlash(entry?.list?.addUrl || dataUrl),
      edit: this.tableSchema.stripLeadingSlash(entry?.list?.editUrl || dataUrl)
    });

    const configuredPayload = entry?.list?.payload ?? entry?.list?.dataPayload;
    if (configuredPayload) {
      this.payloadBySchemaKey.set(key, configuredPayload);
    } else {
      this.payloadBySchemaKey.delete(key);
    }

    const addPayload = entry?.list?.addPayload ?? entry?.list?.add_payload;
    const editPayload = entry?.list?.editPayload ?? entry?.list?.edit_payload;
    this.addEditPayloadBySchemaKey.set(key, { addPayload, editPayload });

    this.tableMetaBySchemaKey.set(key, {
      title: entry?.title || '',
      add: !!entry?.list?.add,
      edit: !!entry?.list?.edit,
      csv: !!entry?.download?.csv,
      pdf: !!entry?.download?.pdf,
      add_routeurl: entry?.list?.add_routeurl || '',
      edit_routeurl: entry?.list?.edit_routeurl || '',
      filter: entry?.filter ?? (entry as any)?.list?.filter ?? (entry as any)?.filterFlags,
      load_after_filter: !!(
        entry?.list?.load_after_filter ??
        entry?.list?.loadAfterFilter ??
        entry?.filter?.load_after_filter ??
        entry?.filter?.loadAfterFilter ??
        entry?.load_after_filter ??
        entry?.loadAfterFilter ??
        false
      ),
      pagination: entry?.list?.pagination !== undefined
        ? !!entry?.list?.pagination
        : entry?.pagination !== undefined
          ? !!entry?.pagination
          : (entry?.list?.dataUrl === 'SitesList' || reportKey === 'SitesList' ? false : true)
    });

    // site_id used for dropdownUrl option lookups: prefer the explicit arg,
    // fall back to whatever the currently-active payload carries.
    const effectiveSiteId = siteId ?? this.payload?.site_id;

    const modalSchema = await this.tableSchema.buildModalFieldsFromTableConfigColumns(
      columns,
      effectiveSiteId
    );

    this.formSchemaBySchemaKey.set(key, modalSchema);
    this.formSchema$.next(modalSchema);
  }

  private processResponse(data: any) {
    const list = data.Data.list || [];
    this.totalCount = Number(data.Data.total || data.Data.count);
    this.totalCount$.next(this.totalCount);

    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    const builtHeader = this.headerBySchemaKey.get(key);

    if (builtHeader?.length) {
      // Table-Configuration driven tab — the dataUrl response may not carry
      // its own header, so use the one built from Table-Configuration's columns.
      data.Data.header = builtHeader;
    } else {
      const cachedSchemaColumns = this.schemaColumnsByReportKey.get(this.reportKey);
      if (cachedSchemaColumns && data?.Data?.header) {
        data.Data.header = this.tableSchema.mergeConfigColumnsIntoHeader(data.Data.header, cachedSchemaColumns);
      }
    }

    if (data?.Data?.header && this.enableEdit) {
      console.log(this.reportKey);

      const tableMeta = this.tableMetaBySchemaKey.get(key);
      data.Data.header = this.aggrid.addActionColumn(
        data.Data.header,
        configId || this.reportKey,
        tableMeta?.edit_routeurl || '',
        this.payload?.site_id || ''
      );
    }

    while (this.fullListData.length < this.totalCount) {
      this.fullListData.push({ id: `dummy-${this.fullListData.length}`, isDummy: true });
    }

    list.forEach((item: any, i: number) => {
      const index = this.offset + i;
      if (index < this.totalCount) {
        this.fullListData[index] = { ...item, isDummy: false };
      }
    });

    const isReprint = this.enableCheckbox ||
      this.reportKey?.toLowerCase().includes('reprint') ||
      this.reportKey?.toLowerCase().includes('receipt');

    const allowHeaderSelectAll = isReprint ? false : (this.allowHeaderSelectAll !== false);

    this.aggrid.processTableData({
      ...data,
      Data: { ...data.Data, list: [...this.fullListData] }
    }, '', false, isReprint, allowHeaderSelectAll);
  }

  private isPayloadChanged(
    oldPayload: any,
    newPayload: any
  ): boolean {
    if (!oldPayload) return true;

    const keys = new Set([
      ...Object.keys(oldPayload),
      ...Object.keys(newPayload)
    ]);

    for (const key of keys) {
      if (oldPayload[key] !== newPayload[key]) {
        return true;
      }
    }

    return false;
  }
  sitename: string = ''
  downloadCSV(destroy$: any, showtrue: any[] = [], sitename: string = '') {
    if (!this.payload || !this.reportKey) {
      console.warn('CSV download aborted: payload not initialized');
      return;
    }

    this.sitename = sitename
    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    const tableMeta = this.tableMetaBySchemaKey.get(key);
    const isLoadAfterFilter = !!(tableMeta?.load_after_filter || this.payload?.load_after_filter);
    const isPaginationActive = tableMeta?.pagination !== undefined
      ? tableMeta.pagination
      : (this.payload?.pagination !== undefined
        ? !!this.payload.pagination
        : (this.reportKey === 'SitesList' ? false : true));

    const payloaddownload: any = { ...this.payload };
    if (isPaginationActive) {
      const countTotal = isLoadAfterFilter ? 10000 : Number(this.totalCount > 0 ? this.totalCount : (this.totalCount$.value > 0 ? this.totalCount$.value : 10000));
      const limit = countTotal > 0 ? countTotal : 10000;
      payloaddownload.Limit = limit;
      payloaddownload.per_page = limit;
      payloaddownload.Offset = 0;
      payloaddownload.page = 1;
    } else {
      delete payloaddownload.Limit;
      delete payloaddownload.limit;
      delete payloaddownload.Offset;
      delete payloaddownload.offset;
      delete payloaddownload.per_page;
      delete payloaddownload.page;
    }

    const extraPayload = this.payloadBySchemaKey.get(key);
    const effectiveDownloadPayload = extraPayload && typeof extraPayload === 'object'
      ? { ...extraPayload, ...payloaddownload }
      : payloaddownload;

    console.log(effectiveDownloadPayload);

    this.api.TowerDetailsTableData(
      this.getEffectiveDataUrl(),   // was: this.reportKey
      effectiveDownloadPayload
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.handleCSVResponse(data, showtrue),
        error: (err) => console.error('❌ CSV download failed:', err)
      });
  }

  private filterHeadersByVisibility(headers: any[], showtrue: any[]) {
    if (!Array.isArray(showtrue) || !showtrue.length) {
      return headers.filter((h: any) => h.show !== false && h.download !== false && h.display !== false);
    }

    const visibleFields = new Set(
      showtrue
        .filter(col => col && col.hide === false)
        .map(col => col.field || col.colId)
        .filter(Boolean)
    );

    return headers.filter((h: any) =>
      visibleFields.has(h.key) && h.download !== false
    );
  }

  /**
   * Schema-driven configuration endpoints commonly return rows without a
   * `Data.header`; the grid gets its headers from Table-Configuration instead.
   * Exports use a fresh request, so they must apply the same cached-header
   * fallback before deciding whether there is anything to download.
   */
  private getExportHeaders(data: any): any[] {
    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    const builtHeader = this.headerBySchemaKey.get(key);

    if (builtHeader?.length) {
      return builtHeader;
    }

    const responseHeaders = data?.Data?.header;
    if (Array.isArray(responseHeaders) && responseHeaders.length) {
      const cachedSchemaColumns = this.schemaColumnsByReportKey.get(this.reportKey);
      if (cachedSchemaColumns) {
        return this.tableSchema.mergeConfigColumnsIntoHeader(responseHeaders, cachedSchemaColumns);
      }
      return responseHeaders;
    }

    return [];
  }

  private filterRowDataByHeaders(list: any[], headers: any[]) {
    const allowedKeys = new Set(headers.map((h: any) => h.key));

    return list.map(row => {
      const filtered: any = {};
      for (const key of allowedKeys) {
        filtered[key] = row[key];
      }
      return filtered;
    });
  }


  async applyHeaderUrlMappingsForExport(
    headers: any[],
    rows: any[],
  ): Promise<any[]> {

    if (!headers?.length || !rows?.length) {
      return rows;
    }
    const urlHeaders = headers.filter(h =>
      h?.key && h?.MapByUrl && h?.MapByKey && h?.MapByValue
    );

    if (!urlHeaders.length) {
      return rows;
    }

    let updatedRows = [...rows];
    const encodedSite = localStorage.getItem('site_idLogin');
    const siteId = this.payload?.site_id || rows[0]?.site_id || (encodedSite ? atob(encodedSite) : '');

    for (const header of urlHeaders) {
      const cleanUrl = header.MapByUrl?.replace(/^\/+/, '') || '';
      const payloadKeyStr = header.MapByPayload ? JSON.stringify(header.MapByPayload) : '';
      const cacheKey = `${cleanUrl}_${siteId}_${payloadKeyStr}`;
      let lookup: Record<string, any> = {};

      try {
        // ---------- CACHE ----------
        if (this.mapByUrlCache.has(cacheKey)) {
          lookup = this.mapByUrlCache.get(cacheKey);
        }
        // ---------- API ----------
        else {
          let res: any;

          try {
            let params: any = { site_id: siteId, Limit: 10000, Offset: 0 };
            if (header.MapByPayload && typeof header.MapByPayload === 'object') {
              params = { ...params, ...header.MapByPayload };
            }

            res = await firstValueFrom(
              this.http
                .get<any>(`${environment.apiURL}/${cleanUrl}`, {
                  params
                })
                .pipe(
                  timeout(8000),
                  catchError(() => of(null))
                )
            );
          } catch {
            continue;
          }

          const list = res?.Data?.list ?? res?.Data ?? res ?? [];

          if (!Array.isArray(list) || !list.length) {
            updatedRows = updatedRows.map(row => ({
              ...row,
              [header.key]: 'N/A'
            }));
            continue;
          }

          const mapByKey = typeof header.MapByKey === 'string' ? header.MapByKey : 'id';
          const mapByValue = header.MapByValue || 'name';

          lookup = list.reduce((acc: any, item: any) => {
            const key = item[mapByKey];
            const value = item[mapByValue];
            if (key != null && value != null) {
              acc[key] = value;
              acc[String(key)] = value;
            }
            return acc;
          }, {});

          this.mapByUrlCache.set(cacheKey, lookup);
        }

        // ---------- APPLY ----------
        updatedRows = updatedRows.map(row => {
          const val = row[header.key];
          if (
            val !== undefined &&
            val !== null &&
            val !== '' &&
            val !== '---' &&
            val !== 'N/A'
          ) {
            const mapped = lookup[val] ?? lookup[String(val)];
            if (mapped !== null && mapped !== undefined) {
              return {
                ...row,
                [header.key]: mapped
              };
            }
          }
          return {
            ...row,
            [header.key]: 'N/A'
          };
        });

      } catch {
        updatedRows = updatedRows.map(row => ({
          ...row,
          [header.key]: 'N/A'
        }));
        continue;
      }
    }

    return updatedRows;
  }

  private async handleCSVResponse(data: any, showtrue: any[] = []) {
    if (data?.Status !== 'Success') {
      this.toast.showToast('CSV download failed: the server did not return a successful response.', 'colored', 5000, 'danger');
      return;
    }

    let headers = this.getExportHeaders(data);
    let list = data?.Data?.list || [];

    if (!headers.length || !list.length) {
      this.toast.showToast('CSV download failed: no exportable data was returned.', 'colored', 5000, 'warning');
      return;
    }

    /* ----------------------------------------
       Fix sitename & company_name from siteList
       ---------------------------------------- */
    const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
    if (Array.isArray(siteList) && siteList.length) {
      const siteMap = new Map(
        siteList.map((s: any) => [String(s.site_id), s])
      );

      list = list.map((row: any) => {
        const site = siteMap.get(String(row.site_id || row.siteid)) || siteList[0];
        if (site) {
          if (!row.site_name) row.site_name = site.site_name || '';
          if (!row.short_code) row.short_code = site.short_code || '';
          if (!row.company_name || row.company_name === '---') row.company_name = site.facility_name || site.site_name || '';
          if (!row.facility_name || row.facility_name === '---') row.facility_name = site.facility_name || site.site_name || '';
        }
        return row;
      });
    }

    /* ----------------------------------------
       Apply header mappings
       ---------------------------------------- */
    list = this.aggrid.applyHeaderKeyMappings(headers, list) || [];
    if (!list.length) return;
    list = await this.applyHeaderUrlMappingsForExport(
      headers,
      list,
    );
    /* ----------------------------------------
       🔑 Apply hide/show logic
       ---------------------------------------- */
    headers = this.filterHeadersByVisibility(headers, showtrue);
    list = this.filterRowDataByHeaders(list, headers);

    if (!headers.length || !list.length) {
      this.toast.showToast('CSV download failed: no exportable columns or rows are available.', 'colored', 5000, 'warning');
      return;
    }

    list = this.appendTotalRow(headers, list, 'csv');

    const csv = this.convertToCSVUsingHeader(headers, list);
    this.setSiteNameFromStorage()

    const fileName = `${this.downloadheader}-${this.sitename}.csv`;
    this.triggerCSVDownload(csv, fileName);
  }

  private appendTotalRow(headers: any[], list: any[], target: 'pdf' | 'csv' | 'table' = 'pdf'): any[] {
    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    const tableMeta = this.tableMetaBySchemaKey.get(key);
    const isLoadAfterFilter = !!(tableMeta?.load_after_filter || this.payload?.load_after_filter);

    // After-filter tables do not append total, download as is
    if (isLoadAfterFilter) {
      return list;
    }

    const isReprint = this.enableCheckbox ||
      this.reportKey?.toLowerCase().includes('reprint') ||
      this.reportKey?.toLowerCase().includes('receipt') ||
      this.downloadheader?.toLowerCase().includes('reprint') ||
      this.downloadheader?.toLowerCase().includes('receipt');

    const isAmountCol = (h: any) => {
      const k = (h.key || '').toLowerCase();
      const n = (h.name || '').toLowerCase();
      return k === 'amount' || k === 'total_amount' || k === 'recharge_amount' || k === 'paid_amount' ||
        k.includes('amount') || k.includes('amt') || n.includes('amount') || n.includes('amt');
    };

    const isTargetTotalCol = (h: any) => {
      if (target === 'pdf') {
        if (h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true' || h.export_total === true || h.exportTotal === true) {
          return true;
        }
        if (h.pdf_total === false || h.pdf_total === 'false' || h.pdfTotal === false || h.pdfTotal === 'false' || h.export_total === false || h.exportTotal === false) {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true || (isReprint && isAmountCol(h));
      } else if (target === 'csv') {
        if (h.csv_total === true || h.csv_total === 'true' || h.csvTotal === true || h.csvTotal === 'true' || h.export_total === true || h.exportTotal === true || h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true') {
          return true;
        }
        if (h.csv_total === false || h.csv_total === 'false' || h.csvTotal === false || h.csvTotal === 'false' || h.export_total === false || h.exportTotal === false) {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true || (isReprint && isAmountCol(h));
      } else {
        if (h.table_total === true || h.table_total === 'true' || h.tableTotal === true || h.tableTotal === 'true' || h.grid_total === true || h.grid_total === 'true' || h.gridTotal === true || h.gridTotal === 'true') {
          return true;
        }
        if (h.table_total === false || h.table_total === 'false' || h.tableTotal === false || h.tableTotal === 'false' || h.grid_total === false || h.grid_total === 'false' || h.gridTotal === false || h.gridTotal === 'false') {
          return false;
        }
        if (h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true') {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true || (isReprint && isAmountCol(h));
      }
    };

    const totalHeaders = headers.filter(isTargetTotalCol);
    if (!totalHeaders.length) return list;

    const totalRow: any = {};
    totalRow['_isTotal'] = true;

    headers.forEach((h, index) => {
      if (isTargetTotalCol(h)) {
        const sum = list.reduce((acc, row) => {
          const val = parseFloat(row[h.key]);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
        totalRow[h.key] = parseFloat(sum.toFixed(2));
      } else {
        totalRow[h.key] = index === 0 ? 'Total' : '';
      }
    });

    return [...list, totalRow];
  }

  private convertToCSVUsingHeader(
    headers: Array<{ name: string; key: string; }>
    , data: any[]
  ): string {

    // 🔥 Filter out siteid column from CSV output
    const validHeaders = headers.filter(h =>
      h.key?.toLowerCase() !== 'siteid'
    );


    /* --------------------------------------------------
       🔹 Build site lookup map from localStorage
       -------------------------------------------------- */
    const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');

    const siteMap = new Map<string, any>();
    siteList.forEach((s: any) => {
      if (s.site_id) {
        siteMap.set(String(s.site_id), s);
      }
    });

    // CSV header row
    const headerRow = validHeaders
      .map(h => `"${h.name}"`)
      .join(',');

    // CSV data rows
    const rows = data.map(row =>
      validHeaders
        .map(h => {
          let value = row[h.key];

          /* --------------------------------------------
             🔹 Fallback mapping for site_name / short_code
             -------------------------------------------- */
          if (
            (value === undefined || value === null || value === '') &&
            row.siteid &&
            (h.key === 'site_name' || h.key === 'short_code')
          ) {
            const site = siteMap.get(String(row.siteid));
            value = site?.[h.key] ?? '';
          }

          if (!row._isTotal && (value === undefined || value === null || value === '' || value === '---')) {
            value = 'N/A';
          }

          return `"${String(value ?? '').replace(/"/g, '""')}"`;
        })
        .join(',')
    );

    return [headerRow, ...rows].join('\n');
  }

  private triggerCSVDownload(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  downloadPDF(destroy$: any, showtrue: any[] = []) {
    if (!this.payload || !this.reportKey) {
      console.warn('PDF download aborted: payload not initialized');
      return;
    }
    const configId = this.payload?.config_id || '';
    const key = this.schemaKey(this.reportKey, configId);
    const tableMeta = this.tableMetaBySchemaKey.get(key);
    const isLoadAfterFilter = !!(tableMeta?.load_after_filter || this.payload?.load_after_filter);
    const isPaginationActive = tableMeta?.pagination !== undefined
      ? tableMeta.pagination
      : (this.payload?.pagination !== undefined
        ? !!this.payload.pagination
        : (this.reportKey === 'SitesList' ? false : true));

    const payloaddownload: any = { ...this.payload };
    if (isPaginationActive) {
      const countTotal = isLoadAfterFilter ? 10000 : Number(this.totalCount > 0 ? this.totalCount : (this.totalCount$.value > 0 ? this.totalCount$.value : 10000));
      const limit = countTotal > 0 ? countTotal : 10000;
      payloaddownload.Limit = limit;
      payloaddownload.per_page = limit;
      payloaddownload.Offset = 0;
      payloaddownload.page = 1;
    } else {
      delete payloaddownload.Limit;
      delete payloaddownload.limit;
      delete payloaddownload.Offset;
      delete payloaddownload.offset;
      delete payloaddownload.per_page;
      delete payloaddownload.page;
    }

    const extraPayload = this.payloadBySchemaKey.get(key);
    const effectiveDownloadPayload = extraPayload && typeof extraPayload === 'object'
      ? { ...extraPayload, ...payloaddownload }
      : payloaddownload;

    console.log(effectiveDownloadPayload);
    this.api.TowerDetailsTableData(
      this.getEffectiveDataUrl(),
      effectiveDownloadPayload
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.handlePDFResponse(data, showtrue),
        error: (err) => {
          console.error('❌ PDF download failed:', err);
          this.toast.showToast('PDF download failed: ' + err, 'colored', 5000, 'danger');
        }
      });
  }

  private async handlePDFResponse(data: any, showtrue: any[] = []) {
    if (data?.Status !== 'Success') {
      this.toast.showToast('PDF download failed: the server did not return a successful response.', 'colored', 5000, 'danger');
      return;
    }

    let headers = this.getExportHeaders(data);
    let list = data?.Data?.list || [];

    const totalCount = Number(data?.Data?.total ?? data?.Data?.count ?? data?.total ?? data?.count ?? list.length ?? 0);
    if (totalCount > 0) {
      this.totalCount = totalCount;
      this.totalCount$.next(totalCount);
    }

    if (!headers.length || !list.length) {
      this.toast.showToast('PDF download failed: no exportable data was returned.', 'colored', 5000, 'warning');
      return;
    }

    /* ---------------- Site Name & Company Name Fix ---------------- */
    const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
    if (Array.isArray(siteList) && siteList.length) {
      const siteMap = new Map(
        siteList.map((s: any) => [String(s.site_id), s])
      );

      list = list.map((row: any) => {
        const site = siteMap.get(String(row.site_id || row.siteid)) || siteList[0];
        if (site) {
          if (!row.site_name) row.site_name = site.site_name || '';
          if (!row.short_code) row.short_code = site.short_code || '';
          if (!row.company_name || row.company_name === '---') row.company_name = site.facility_name || site.site_name || '';
          if (!row.facility_name || row.facility_name === '---') row.facility_name = site.facility_name || site.site_name || '';
        }
        return row;
      });
    }

    /* ---------------- Apply Key Mapping ---------------- */
    list = this.aggrid.applyHeaderKeyMappings(headers, list) || [];
    if (!list.length) return;

    list = await this.applyHeaderUrlMappingsForExport(headers, list);

    /* ---------------- Apply Hide/Show ---------------- */
    headers = this.filterHeadersByVisibility(headers, showtrue);
    list = this.filterRowDataByHeaders(list, headers);

    if (!headers.length || !list.length) {
      this.toast.showToast('PDF download failed: no exportable columns or rows are available.', 'colored', 5000, 'warning');
      return;
    }

    list = this.appendTotalRow(headers, list, 'pdf');

    this.setSiteNameFromStorage()
    this.generatePDF(headers, list);
  }
  private generatePDF(
    headers: Array<{ name: string; key: string }>,
    data: any[]
  ) {
    const validHeaders = headers.filter(h =>
      h.key?.toLowerCase() !== 'siteid'
    );

    // Decide page size based on column count
    let pageFormat: any = 'a4';

    const columnCount = validHeaders.length;

    if (columnCount <= 10) {
      pageFormat = 'a4';
    } else if (columnCount <= 16) {
      pageFormat = 'a3';
    } else if (columnCount <= 26) {
      pageFormat = 'a2';
    } else if (columnCount <= 32) {
      pageFormat = 'a1'
    } else {
      pageFormat = 'a0'
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: pageFormat
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 20; // left + right margin (10 + 10)
    const equalColumnWidth = usableWidth / columnCount;
    const columnStyles: any = {};

    validHeaders.forEach((_, index) => {
      columnStyles[index] = { cellWidth: equalColumnWidth };
    });

    let dynamicFontSize = Math.round(equalColumnWidth * 0.45);

    const fontLimits: any = {
      a4: { min: 6, max: 11 },
      a3: { min: 7, max: 13 },
      a2: { min: 8, max: 15 },
      a1: { min: 9, max: 18 }
    };

    const limits = fontLimits[String(pageFormat).toLowerCase()] || fontLimits.a4;

    dynamicFontSize = Math.max(
      limits.min,
      Math.min(dynamicFontSize, limits.max)
    );
    const payload = this.payload || {};
    console.log(payload);

    const BLACKLIST = ['status', 'state', 'id', 'offset', 'limit', 'site_id', 'site_ids', 'site', 'config_id', 'tower_id', 'loc_id', 'loc_from', 'loc_to', 'loc_idfrom', 'loc_idto', 'from_location_id', 'to_location_id', 'location_id', 'other_type', 'alarm_id', 'water_loc_id', 'otherType', 'orderBy', 'per_page', 'page', 'fromdate', 'todate'];

    const runDate = new Date().toLocaleString();

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');

    doc.text(`Site : ${this.sitename || '-'}`, 14, 10);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(
      this.downloadheader,
      pageWidth / 2,
      12,
      { align: 'center' }
    );

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    doc.text(`Page 1 of 1`, pageWidth - 14, 10, { align: 'right' });
    doc.text(`Run Date : ${runDate}`, pageWidth - 14, 16, { align: 'right' });

    let filterY = 24;
    doc.setFontSize(dynamicFontSize);

    Object.entries(payload).forEach(([key, value]) => {
      if (BLACKLIST.includes(key.toLowerCase())) return;
      if (!value) return;

      const lowerKey = key.toLowerCase();
      if ((lowerKey === 'selected_date' || lowerKey === 'selecteddate') && ((payload as any)['date'] || (payload as any)['Date'])) {
        return;
      }

      const label = this.formatKey(key);

      doc.text(`${label} : ${value}`, 14, filterY);
      filterY += 6;
    });

    doc.line(10, filterY, pageWidth - 10, filterY);

    const tableHeaders = validHeaders.map(h => h.name);

    const tableRows = data.map(row =>
      validHeaders.map(h => {
        if (row._isTotal) {
          return row[h.key] ?? '';
        }
        const val = row[h.key];
        return (val === undefined || val === null || val === '' || val === '---') ? 'N/A' : val;
      })
    );

    autoTable(doc, {
      head: [tableHeaders],
      body: tableRows,
      startY: filterY + 5,
      styles: {
        fontSize: dynamicFontSize,
        cellPadding: 2,
        valign: 'middle'
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 0,
        lineWidth: 0.2,
        fontSize: dynamicFontSize + 1
      },
      columnStyles,

      theme: 'grid',
      margin: { left: 10, right: 10 }
    });


    doc.save(`${this.downloadheader}-${this.sitename}.pdf`);
  }
  private formatKey(key: string): string {
    const customLabels: Record<string, string> = {
      unit_from: 'From Unit',
      unit_to: 'To Unit',
      from_unit: 'From Unit',
      to_unit: 'To Unit',
      unit: 'Unit',
      search: 'Search By',
      search_by: 'Search By',
      searchby: 'Search By',
      filter_search: 'Search By',
      filtersearch: 'Search By',
      search_text: 'Search By',
      searchtext: 'Search By',
      search_term: 'Search By',
      searchterm: 'Search By',
      search_val: 'Search By',
      searchval: 'Search By',
      search_value: 'Search By',
      searchvalue: 'Search By',
      search_query: 'Search By',
      searchquery: 'Search By',
      search_input: 'Search By',
      searchinput: 'Search By'
    };
    const lower = key.toLowerCase();
    if (customLabels[lower]) {
      return customLabels[lower];
    }
    if (lower === 'search' || lower.startsWith('search') || lower.endsWith('search') || lower.includes('_search') || lower.includes('search_')) {
      return 'Search By';
    }
    return key
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  private setSiteNameFromStorage(): void {

    let siteIdsRaw: string | null = this.payload?.site_ids || this.payload?.site_id || this.payload?.site || null;
    const siteListRaw = localStorage.getItem('siteList');
    if (!siteListRaw) {
      this.sitename = '';
      return;
    }

    try {
      const siteList = JSON.parse(siteListRaw);

      if (!siteIdsRaw) {
        const encoded = localStorage.getItem('site_idLogin');
        siteIdsRaw = encoded ? atob(encoded) : null;
      }

      if (!siteIdsRaw) {
        this.sitename = '';
        return;
      }

      const siteIds = String(siteIdsRaw)
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);

      let matchedSites = siteList.filter((site: any) =>
        siteIds.includes(String(site.site_id))
      );

      if (matchedSites.length === 0) {
        const encoded = localStorage.getItem('site_idLogin');
        const decoded = encoded ? atob(encoded) : null;

        if (decoded) {
          matchedSites = siteList.filter((site: any) =>
            String(site.site_id) === decoded
          );
        }
      }

      const matchedNames = matchedSites.map((site: any) => site.site_name);

      this.sitename =
        matchedNames.length > 3
          ? `${matchedNames.slice(0, 3).join(', ')} +${matchedNames.length - 3} more`
          : matchedNames.join(', ');

    } catch (e) {
      console.error('Invalid siteList in localStorage');
      this.sitename = '';
    }
  }


}
